import { useId } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Chip, Rating, Surface } from '@nexvia-logistics/ui';
import { ENDED_BY_LABELS, STORAGE_STATUS_LABELS, type StorageQuote, type StorageReview } from '@/shared/storage-api';
import { formatCalendarDate, formatDateTime } from '@/shared/format';

type QuoteRequest = NonNullable<StorageQuote['request']>;
type ContractedRequest = QuoteRequest & { storageStatus: NonNullable<QuoteRequest['storageStatus']> };

/** NEX-1170 — the request of a quote the client contracted (paid): the one that carries a guardado. */
function contractedRequestOf(quote: StorageQuote): ContractedRequest | null {
  const request = quote.request;
  if (quote.status !== 'accepted' || !request?.storageStatus) return null;
  return request as ContractedRequest;
}

/** Same chip vocabulary as the quote's own `StatusChip`: waiting is outlined, running is active. */
function GuardadoChip({ status }: { status: ContractedRequest['storageStatus'] }) {
  const label = STORAGE_STATUS_LABELS[status];
  switch (status) {
    case 'contracted':
      return (
        <Chip tone="neutral" outlined>
          {label}
        </Chip>
      );
    case 'active':
      return <Chip tone="active">{label}</Chip>;
    case 'ending':
      return <Chip tone="caution">{label}</Chip>;
    default:
      return <Chip tone="neutral">{label}</Chip>;
  }
}

interface ReceptionCallbacks {
  onConfirmReception: () => void;
  onReportNotReceived: () => void;
  /** NEX-1170 (X1) — "El cliente retiró sus cosas": `CotizacionDetailPage` opens the confirmation. */
  onConfirmWithdrawal: () => void;
  /** NEX-1329 — "Calificá al cliente": `CotizacionDetailPage` opens `ClientReviewDialog`. */
  onReviewClient: () => void;
}

interface GuardadoNotices {
  /**
   * NEX-1169 — the depósito has no bank account saved (`GET /storage/me/bank-account` answered
   * `null`, asked by `CotizacionDetailPage`): Nexvia has nowhere to pay this guardado into.
   */
  missingBankAccount?: boolean;
  /**
   * Quote detail page (2026-10-28) — `inset` (default) is a block inside a white list row; on the
   * quote's own page, over the grey page background, it is a `panel` like the page's other cards.
   */
  surface?: 'inset' | 'panel';
}

/** NEX-1169 — the same words as Mi empresa's "Cuenta bancaria" card, with the way there. */
function MissingBankAccountNotice() {
  return (
    <Alert tone="caution">
      <span className="block">Cargá tu cuenta bancaria para que podamos pagarte.</span>
      <Link to="/empresa" className="text-ui-label text-current underline">
        Ir a Mi empresa
      </Link>
    </Alert>
  );
}

type LinkedShipment = NonNullable<StorageQuote['linkedShipment']>;

/**
 * NEX-1168 (plan 2026-10-20 Tarea 2.7) — the shipment the client sent straight to this depósito,
 * while the guardado waits for its things: the delivery code to hand the carrier (entering it
 * confirms the reception by itself), or, once the code is no longer active, that the reception is
 * confirmed from here. In place of the generic "confirmá la recepción" line.
 */
function LinkedShipmentNotice({ shipment }: { shipment: LinkedShipment }) {
  if (!shipment.deliveryCode) {
    return (
      <p className="text-ui-small text-on-surface-variant">
        El código ya no está disponible; confirmá la recepción acá cuando lleguen las cosas.
      </p>
    );
  }
  return (
    <Alert tone="info">
      Código de entrega: <strong className="tabular-nums">{shipment.deliveryCode}</strong>. Dáselo al transportista cuando
      recibas las cosas.
    </Alert>
  );
}

/** NEX-1329 — one side's review, read-only: the stars (named for screen readers) and the comment. */
function ReviewSummary({ title, review }: { title: string; review: StorageReview }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-ui-small text-on-surface-variant">{title}</span>
        <Rating value={review.rating} label={title} size={16} />
      </div>
      {review.comment && <p className="text-ui-small text-on-surface-placeholder">{review.comment}</p>}
    </div>
  );
}

/**
 * NEX-1329 (decision A6) — the guardado's mutual reviews, the depósito's side
 * (`request.canReview`/`myReview`/`theirReview`): "Calificá al cliente" from the reception until the
 * depósito reviews, then its own review; the client's review of the depósito whenever it exists.
 */
function GuardadoReviews({ request, onReviewClient }: { request: ContractedRequest; onReviewClient: () => void }) {
  const { canReview, myReview, theirReview } = request;
  if (!canReview && !myReview && !theirReview) return null;
  return (
    <div className="flex flex-col gap-2 pt-1">
      {canReview && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-ui-small text-on-surface-variant">¿Cómo fue el cliente? Contanos con una calificación.</p>
          <Button type="button" variant="outline" size="xs" showArrow={false} onClick={onReviewClient}>
            Calificá al cliente
          </Button>
        </div>
      )}
      {myReview && <ReviewSummary title="Tu calificación del cliente" review={myReview} />}
      {theirReview && <ReviewSummary title="La calificación del cliente a tu depósito" review={theirReview} />}
    </div>
  );
}

/**
 * NEX-1170 — the guardado of the quote the client contracted, on the quote's own page: its state
 * (contracted → active → ending → ended, `GET /storage/quotes/mine`'s `request.storageStatus`), when
 * the things were received and the end date, plus what the client asked for (baja, retiro).
 * Nothing on any other quote (the API sends the guardado fields as `null` there).
 *
 * While contracted, the depósito confirms the reception or reports it never arrived (decision A4):
 * the dialogs and the calls live in `CotizacionDetailPage`, the quote's own page. A "No lo recibí" report
 * leaves the guardado contracted, so both buttons stay: the things may still arrive.
 *
 * NEX-1170 (X1) — while active or ending, "El cliente retiró sus cosas" ends the guardado (same
 * dialog); once ended, the line says when and how (`endedBy`: this depósito, the client, or the
 * API's automatic close at the end of the notice).
 *
 * NEX-1329 — from the reception on it also carries the mutual reviews (`GuardadoReviews`).
 *
 * NEX-1169 (plan 2026-10-20, Tarea 1.10) — with no bank account saved (`missingBankAccount`), it
 * says "Cargá tu cuenta bancaria para que podamos pagarte." with a link to Mi empresa, in every
 * state of the guardado: what the client paid is owed to the depósito until Nexvia transfers it.
 *
 * NEX-1168 (plan 2026-10-20 Tarea 2.7) — while contracted, a shipment sent straight to this depósito
 * (`quote.linkedShipment`) shows its delivery code, or says it is no longer available
 * (`LinkedShipmentNotice`); the reception buttons stay either way. A reception the carrier confirmed
 * with the code reads "con el código de entrega".
 */
export function QuoteGuardado({ quote, ...props }: ReceptionCallbacks & GuardadoNotices & { quote: StorageQuote }) {
  const request = contractedRequestOf(quote);
  return request ? <GuardadoDetail request={request} linkedShipment={quote.linkedShipment} {...props} /> : null;
}

const RECEPTION_CONFIRMED_BY_SUFFIX: Record<NonNullable<ContractedRequest['receptionConfirmedBy']>, string> = {
  storage: '',
  backoffice: ' · confirmado por Nexvia',
  delivery_code: ' · con el código de entrega'
};

function GuardadoDetail({
  request,
  linkedShipment,
  missingBankAccount = false,
  surface = 'inset',
  onConfirmReception,
  onReportNotReceived,
  onConfirmWithdrawal,
  onReviewClient
}: ReceptionCallbacks & GuardadoNotices & { request: ContractedRequest; linkedShipment: StorageQuote['linkedShipment'] }) {
  const headingId = useId();
  const receivedAt = formatDateTime(request.receivedAt);
  const endsAt = formatCalendarDate(request.endsAt);
  const notReceivedAt = formatDateTime(request.notReceivedAt);
  const cancelRequestedAt = formatDateTime(request.cancelRequestedAt);
  const withdrawalRequestedAt = formatDateTime(request.withdrawalRequestedAt);
  const endedAt = formatDateTime(request.endedAt);
  const status = request.storageStatus;
  // On the quote's own page the guardado is one of the page's cards, right under its h1.
  const Heading = surface === 'panel' ? 'h2' : 'h3';

  // NEX-1165 design pass (decision D-3.2) — an inset `Surface` under the accepted quote's row; the
  // group (named by its heading) is the element inside it, since `Surface` takes no role/aria props.
  return (
    <Surface variant={surface} padding={surface === 'inset' ? 'sm' : 'md'}>
      <div role="group" aria-labelledby={headingId} className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Heading id={headingId} className="text-ui-label text-on-surface">
            Guardado
          </Heading>
          <GuardadoChip status={status} />
        </div>

        {missingBankAccount && <MissingBankAccountNotice />}

        {status === 'contracted' &&
          (linkedShipment ? (
            <LinkedShipmentNotice shipment={linkedShipment} />
          ) : (
            <p className="text-ui-small text-on-surface-variant">
              Cuando recibas las cosas del cliente, confirmá la recepción: ahí empieza el guardado.
            </p>
          ))}

        {receivedAt && (
          <p className="text-ui-small text-on-surface-variant">
            {`Recibido el ${receivedAt}${request.receptionConfirmedBy ? RECEPTION_CONFIRMED_BY_SUFFIX[request.receptionConfirmedBy] : ''}`}
          </p>
        )}
        {request.receptionNote && (
          <p className="text-ui-small text-on-surface-placeholder">{`Nota de recepción: ${request.receptionNote}`}</p>
        )}
        {status === 'active' && endsAt && <p className="text-ui-small text-on-surface-variant">{`Termina el ${endsAt}`}</p>}
        {status === 'ended' && (endedAt || request.endedBy) && (
          <p className="text-ui-small text-on-surface-variant">
            {[endedAt ? `Terminó el ${endedAt}` : 'Terminó', request.endedBy ? ENDED_BY_LABELS[request.endedBy] : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}

        {status === 'ending' && (
          <Alert tone="caution">
            {`El cliente pidió la baja${cancelRequestedAt ? ` el ${cancelRequestedAt}` : ''}.${endsAt ? ` El guardado termina el ${endsAt}.` : ''}`}
          </Alert>
        )}

        {withdrawalRequestedAt && status !== 'ended' && (
          <Alert tone="info">{`El cliente solicitó el retiro de sus cosas el ${withdrawalRequestedAt}.`}</Alert>
        )}

        {status === 'contracted' && notReceivedAt && (
          <Alert tone="caution">
            <span className="block">
              {`El ${notReceivedAt} avisaste que no recibiste la carga. Nexvia fue avisada y se va a comunicar con vos para resolverlo.`}
            </span>
            {request.notReceivedReason && <span className="block">{`Motivo: ${request.notReceivedReason}`}</span>}
          </Alert>
        )}

        {status === 'contracted' && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" size="xs" showArrow={false} onClick={onConfirmReception}>
              Confirmar recepción
            </Button>
            <Button type="button" variant="outline" size="xs" showArrow={false} onClick={onReportNotReceived}>
              No lo recibí
            </Button>
          </div>
        )}

        {(status === 'active' || status === 'ending') && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-ui-small text-on-surface-variant">¿El cliente ya se llevó todo? Confirmalo para terminar el guardado.</p>
            <Button type="button" variant="outline" size="xs" showArrow={false} onClick={onConfirmWithdrawal}>
              El cliente retiró sus cosas
            </Button>
          </div>
        )}

        <GuardadoReviews request={request} onReviewClient={onReviewClient} />
      </div>
    </Surface>
  );
}
